import React, { Fragment, forwardRef } from 'react'

import { Box } from '../Box/Box'
import { Icon } from '../Icon/Icon'
import { Inline } from '../Inline/Inline'
import { Stack } from '../Stack/Stack'
import { Text } from '../Text/Text'
import { FieldLabelProps, FieldLabel } from '../FieldLabel/FieldLabel'
import { FieldMessage } from '../FieldMessage/FieldMessage'
import styled from '@emotion/styled'

export interface TextFieldProps
  extends Partial<FieldLabelProps>,
    Partial<Omit<HTMLInputElement, 'min' | 'max'>> {
  search?: boolean
  prefix?: string
  value?: string | undefined
  type?: string
  placeholder?: string
  message?: string
  reserveSpaceForMessage?: boolean
  tone?: 'neutral' | 'positive' | 'negative'
  disabled?: boolean
  big?: boolean
  centerText?: boolean
  background?: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onClear?: () => void
  min?: string | number
  max?: string | number
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      labelWeight = 'strong',
      secondary,
      tertiary,
      tertiaryIcon,
      tertiaryAction,
      description,
      search = false,
      prefix,
      message,
      reserveSpaceForMessage,
      tone = 'neutral',
      disabled = false,
      big = false,
      type = 'text',
      background = 'transparent',
      onChange,
      onBlur,
      onClear,
      centerText,
      ...props
    },
    forwardedRef
  ) => {
    const borderTone = tone === 'negative' ? 'negative' : 'neutral'

    return (
      <Stack space="xs">
        {label && (
          <FieldLabel
            label={label}
            labelWeight={labelWeight}
            secondary={secondary}
            tertiary={tertiary}
            tertiaryIcon={tertiaryIcon}
            tertiaryAction={tertiaryAction}
            description={description}
          />
        )}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="between"
          width="100%"
          height={big ? undefined : 'var(--space--xl)'}
          border={`1px solid var(--textfield-border--${borderTone})`}
          radius="xxs"
          insetSquish={big ? 'm' : 's'}
          background={disabled ? 'var(--textfield-bg--disabled)' : background}
        >
          <Inline space="s" alignY="center">
            {search && (
              <Box display="flex" alignItems="center">
                <Icon name="search" size="s" />
              </Box>
            )}

            {prefix && (
              <Fragment>
                <Text size={big ? 'big' : 'base'}>{prefix}</Text>
                <Box width="1px" height="14px" background="var(--textfield-border--neutral)" />
              </Fragment>
            )}

            <Box />
          </Inline>

          <StyledInputBox
            ref={forwardedRef}
            component="input"
            type={type}
            width="100%"
            border="none"
            onChange={onChange}
            onBlur={onBlur}
            background={background}
            disabled={disabled}
            style={{
              outline: 'none',
              flex: 1,
              fontSize: big ? '1.6rem' : undefined,
              //@ts-ignore
              textAlign: centerText ? 'center' : undefined
            }}
            {...props}
          />

          {!!props.value && onClear && (
            <Box display="flex" alignItems="center" onClick={onClear} cursor="pointer">
              <Icon name="close" tone="light" size="s" />
            </Box>
          )}
        </Box>

        {message ? (
          <FieldMessage tone={tone === 'neutral' ? 'light' : tone}>{message}</FieldMessage>
        ) : (
          reserveSpaceForMessage && <Box height={12} width="100%" />
        )}
      </Stack>
    )
  }
)

const StyledInputBox = styled(Box)`
  -webkit-box-shadow: 0 0 0 300px transparent inset !important;

  ${(p) =>
    p.background &&
    `
    -webkit-box-shadow: 0 0 0 300px ${p.background} inset !important;
  `}
`
